import { ROLES, type ApiKeyCreada } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, KeyRound, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useI18n } from '../../../i18n/I18nProvider';
import { apiDocsUrl, getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { apiKeysApi } from '../api/apiKeys.api';

export function ApiKeysPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [crearOpen, setCrearOpen] = useState(false);
  const [creada, setCreada] = useState<ApiKeyCreada | null>(null);
  const es = useI18n();

  const { data, isLoading, error } = useQuery({
    queryKey: qk.admin.apiKeys,
    queryFn: () => apiKeysApi.list(),
  });

  const revocar = useMutation({
    mutationFn: (id: string) => apiKeysApi.revocar(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.admin.apiKeys });
      toast.success(es.apiKeys.revocada_exito);
    },
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  const apiKeys = data?.apiKeys ?? [];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <KeyRound className="text-text-marca" size={24} />
            <h1 className="font-titulos text-3xl font-bold text-text-marca">{es.apiKeys.titulo}</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-marca-gris/80">{es.apiKeys.descripcion}</p>
          <a
            href={apiDocsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-xs text-marca-azul hover:underline"
          >
            <ExternalLink size={12} />
            {es.apiKeys.docs}
          </a>
        </div>
        <Button onClick={() => setCrearOpen(true)}>
          <Plus size={16} />
          {es.apiKeys.crear}
        </Button>
      </div>

      {error && (
        <Alert variant="error" className="mt-4">
          {getErrorMessage(error, es.errores.generico.algoSalioMal)}
        </Alert>
      )}
      {isLoading && <p className="mt-4 text-sm text-marca-gris/70">{es.apiKeys.cargando}</p>}

      {data && (
        <div className="mt-4 overflow-x-auto rounded-md border border-marca-neutro bg-surface">
          <table className="w-full text-xs">
            <thead className="border-b border-marca-neutro bg-marca-neutro/30 text-[10px] uppercase text-marca-gris/70">
              <tr>
                <th className="px-2 py-2 text-left">{es.apiKeys.col.nombre}</th>
                <th className="px-2 py-2 text-left">{es.apiKeys.col.prefijo}</th>
                <th className="px-2 py-2 text-left">{es.apiKeys.col.rol}</th>
                <th className="px-2 py-2 text-left">{es.apiKeys.col.estado}</th>
                <th className="px-2 py-2 text-left">{es.apiKeys.col.ultimoUso}</th>
                <th className="px-2 py-2 text-left">{es.apiKeys.col.creadoPor}</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-2 py-6 text-center text-marca-gris/60">
                    {es.apiKeys.vacio}
                  </td>
                </tr>
              ) : (
                apiKeys.map((k) => (
                  <tr key={k.id} className="border-b border-marca-neutro/40">
                    <td className="px-2 py-1.5 font-medium text-text-marca">{k.nombre}</td>
                    <td className="px-2 py-1.5 font-mono text-marca-gris/80">{k.prefijo}</td>
                    <td className="px-2 py-1.5">{k.rol}</td>
                    <td className="px-2 py-1.5">
                      {k.activa ? (
                        <span className="rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success">
                          {es.apiKeys.activa}
                        </span>
                      ) : (
                        <span className="rounded-full bg-marca-neutro px-2 py-0.5 text-[11px] font-semibold text-marca-gris/60">
                          {es.apiKeys.revocada}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-[11px] text-marca-gris/60">
                      {k.ultimoUsoEn
                        ? new Date(k.ultimoUsoEn).toLocaleString('es-AR', {
                            timeZone: 'America/Argentina/Buenos_Aires',
                          })
                        : es.apiKeys.nunca}
                    </td>
                    <td className="px-2 py-1.5 text-marca-gris/70">{k.creadoPor.nombre}</td>
                    <td className="px-2 py-1.5 text-right">
                      {k.activa && (
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={revocar.isPending}
                          onClick={() => {
                            if (window.confirm(es.apiKeys.confirmarRevocar)) revocar.mutate(k.id);
                          }}
                          aria-label={es.apiKeys.revocar}
                        >
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {crearOpen && (
        <CrearApiKeyDialog
          open
          onOpenChange={setCrearOpen}
          onCreada={(c) => {
            void queryClient.invalidateQueries({ queryKey: qk.admin.apiKeys });
            setCrearOpen(false);
            setCreada(c);
          }}
        />
      )}
      {creada && <ApiKeyCreadaDialog creada={creada} onClose={() => setCreada(null)} />}
    </div>
  );
}

function CrearApiKeyDialog({
  open,
  onOpenChange,
  onCreada,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreada: (c: ApiKeyCreada) => void;
}): JSX.Element {
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<(typeof ROLES)[number]>('VISUALIZADOR');
  const es = useI18n();

  const mutation = useMutation({
    mutationFn: () => apiKeysApi.crear({ nombre, rol }),
    onSuccess: (c) => onCreada(c),
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{es.apiKeys.dialog.titulo}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <FormField label={es.apiKeys.dialog.nombre} htmlFor="ak-nombre">
            <Input
              id="ak-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder={es.apiKeys.dialog.nombrePlaceholder}
            />
          </FormField>
          <FormField label={es.apiKeys.dialog.rol} htmlFor="ak-rol">
            <Select value={rol} onValueChange={(v) => setRol(v as (typeof ROLES)[number])}>
              <SelectTrigger id="ak-rol">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {es.apiKeys.dialog.cancelar}
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || nombre.trim().length < 3}
          >
            {es.apiKeys.dialog.crear}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApiKeyCreadaDialog({ creada, onClose }: { creada: ApiKeyCreada; onClose: () => void }): JSX.Element {
  const es = useI18n();
  const [copiado, setCopiado] = useState(false);
  function copiar(): void {
    void navigator.clipboard.writeText(creada.key).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{es.apiKeys.creada.titulo}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Alert variant="warning">{es.apiKeys.creada.aviso}</Alert>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded border border-marca-neutro bg-surface-sunken px-2 py-1.5 font-mono text-xs">
              {creada.key}
            </code>
            <Button type="button" variant="outline" size="sm" onClick={copiar}>
              {copiado ? <Check size={14} /> : <Copy size={14} />}
              {copiado ? es.apiKeys.creada.copiado : es.apiKeys.creada.copiar}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {es.apiKeys.creada.cerrar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
